import type { MasterOrderLine } from "~/@types/orderLines";
import type { MasterOrderDetailsFragment } from "~/gql/queries/masterOrder.generated";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isEqual, remove } from "lodash";

import { MasterLineItemWithWeeks } from "~/@types/masterOrderWeeks";
import { TDateISO } from "~/@types/TDateISO";
import {
	AddMasterLineItemDataInput,
	MasterOrderContactDataInput,
	MasterOrderCustomerDataInput,
	MasterOrdersDocument,
	OrderType
} from "~/gql/graphqlBase.generated";
import masterOrderUnits from "~fulfill/orders/helpers/masterOrderUnits";

export interface MasterOrderInputs {
	masterOrderId: string;
	orderNumber: string;
	poNumber: string;
	salesPersonEmail: string;
	tenantId: string;
	type: OrderType;
}

export type MasterOrderSavedChanges = Partial<
	Record<
		| "poNumber"
		| "type"
		| "salesPersonEmail"
		| "customer"
		| "location"
		| "billToAddress"
		| "firstName"
		| "lastName"
		| "emailAddress"
		| "phoneNumber"
		| "status",
		string
	>
>;

export type StagedMasterLineItem = AddMasterLineItemDataInput & { eachesPerUnit?: number; shippingUomName?: string };

const scheduledEaches = (
	state: { masterOrderStateData?: MasterOrderDetailsFragment },
	lineItem: MasterOrderLine
): number =>
	masterOrderUnits.masterWeekEaches(
		lineItem,
		masterOrderUnits.masterEachesEntry(state.masterOrderStateData?.orderEntryUnit)
	);

interface MasterOrderState {
	masterOrderReviewPending?: boolean;
	masterOrderSavedChanges?: MasterOrderSavedChanges;
	addMasterLineItem: StagedMasterLineItem;
	addMasterLineItemOpen: boolean;
	addMasterLineItemsTableOpen: boolean;
	currency: string;
	masterLineItems?: MasterOrderLine[];
	masterOrderCustomer?: MasterOrderCustomerDataInput;
	masterOrderInputs: MasterOrderInputs;
	masterOrderLineItemsListOpen: boolean;
	masterOrdersRowData: MasterOrdersDocument[];
	masterOrderStateData?: MasterOrderDetailsFragment;
	pickedCustomerId?: string;
}

const initialState: MasterOrderState = {
	masterOrderReviewPending: false,
	masterOrderSavedChanges: {},
	addMasterLineItem: {
		addedBy: "",
		currency: "",
		dateAdded: new Date().toISOString() as TDateISO,
		id: "",
		itemGroup: undefined,
		itemId: "",
		lineNumber: 0,
		name: "",
		orderedQuantity: undefined,
		price: 0.0,
		sku: "",
		upc: ""
	},
	addMasterLineItemOpen: false,
	addMasterLineItemsTableOpen: false,
	currency: "USD",
	masterOrderInputs: {
		masterOrderId: "",
		orderNumber: "",
		poNumber: "",
		salesPersonEmail: "",
		tenantId: "",
		type: OrderType.Po
	},
	masterOrderLineItemsListOpen: false,
	masterOrdersRowData: []
};

export const masterOrderSlice = createSlice({
	initialState,
	name: "masterOrder",
	reducers: {
		addMasterLineItem: (state) => {
			const noteText = state.addMasterLineItem.noteText?.trim();
			const lineItem = {
				...state.addMasterLineItem,
				eaches: 0,
				eachesPerUnit: state.addMasterLineItem.eachesPerUnit ?? 1,
				note: noteText
					? {
							noteText,
							submitDate: new Date().toISOString() as TDateISO,
							submittedBy: state.addMasterLineItem.addedBy || ""
						}
					: undefined,
				shippingUomName: state.addMasterLineItem.shippingUomName ?? "",
				sku: state.addMasterLineItem.sku ?? "",
				upc: state.addMasterLineItem.upc ?? ""
			};
			if (!state.masterLineItems || (state.masterLineItems?.length || 0) === 0) {
				state.masterLineItems = [
					{
						...lineItem,
						lineNumber: 1,
						sku: state.addMasterLineItem.sku ?? "",
						upc: state.addMasterLineItem.upc ?? ""
					}
				];
			} else {
				const maxLineNumber = Math.max(...state.masterLineItems.map((item) => item.lineNumber || 0));
				state.masterLineItems.push({
					...lineItem,
					lineNumber: maxLineNumber + 1,
					sku: state.addMasterLineItem.sku ?? "",
					upc: state.addMasterLineItem.upc ?? ""
				});
			}
			state.addMasterLineItem = initialState.addMasterLineItem;
		},
		clearMasterLineItemsWeekQuantities: (state) => {
			if (state.masterLineItems) {
				state.masterLineItems.forEach((lineItem) => {
					Object.keys(lineItem).forEach((key) => {
						if (key.startsWith("week_") && key.endsWith("_quantity")) {
							delete (lineItem as MasterLineItemWithWeeks)[key as `week_${number}_quantity`];
						}
					});
					lineItem.scheduledQuantity = 0;
				});
			}
		},
		clearMasterLineItemsWeeks: (state, action: PayloadAction<number[]>) => {
			state.masterLineItems?.forEach((lineItem) => {
				for (const week of action.payload) {
					delete (lineItem as MasterLineItemWithWeeks)[`week_${week}_quantity`];
				}
				lineItem.scheduledQuantity = scheduledEaches(state, lineItem);
			});
		},
		clearMasterOrderInfo: (state) => {
			state.masterOrderReviewPending = false;
			state.masterOrderSavedChanges = {};
			state.addMasterLineItem = initialState.addMasterLineItem;
			state.addMasterLineItemOpen = false;
			state.addMasterLineItemsTableOpen = false;
			state.currency = initialState.currency;
			state.masterLineItems = undefined;
			state.masterOrderCustomer = undefined;
			state.masterOrderInputs = initialState.masterOrderInputs;
			state.masterOrderLineItemsListOpen = false;
			state.masterOrderStateData = undefined;
			state.pickedCustomerId = undefined;
		},
		clearMasterOrdersRowData(state) {
			state.masterOrdersRowData = [];
		},
		closeAddMasterLineItem: (state) => {
			state.addMasterLineItemOpen = false;
		},
		closeAddMasterLineItemsTable: (state) => {
			state.addMasterLineItemsTableOpen = false;
		},
		closeMasterOrderLineItemsList: (state) => {
			state.masterOrderLineItemsListOpen = false;
		},
		openAddMasterLineItem: (state) => {
			state.addMasterLineItemOpen = true;
		},
		openAddMasterLineItemsTable: (state) => {
			state.addMasterLineItemsTableOpen = true;
		},
		openMasterOrderLineItemsList: (state) => {
			state.masterOrderLineItemsListOpen = true;
		},
		removeMasterLineItem: (state, action: PayloadAction<string>) => {
			if (!state.masterLineItems) return;
			remove(state.masterLineItems, (item) => item.id === action.payload);
			state.addMasterLineItem = initialState.addMasterLineItem;
			state.addMasterLineItemOpen = false;
		},
		setAddMasterLineItem: (state, action: PayloadAction<Partial<StagedMasterLineItem> | null>) => {
			if (action.payload === null) {
				state.addMasterLineItem = initialState.addMasterLineItem;
				return;
			}
			state.addMasterLineItem = { ...initialState.addMasterLineItem, ...action.payload };
		},
		setMasterLineItems: (state, action: PayloadAction<MasterOrderLine[] | null>) => {
			if (action.payload === null) {
				state.masterLineItems = undefined;
				return;
			}
			state.masterLineItems = [...action.payload];
		},
		setMasterOrderCustomer: (state, action: PayloadAction<MasterOrderCustomerDataInput | null>) => {
			if (action.payload === null) {
				state.masterOrderCustomer = undefined;
				return;
			}
			state.masterOrderCustomer = { ...action.payload };
		},
		setMasterOrderInputs: (state, action: PayloadAction<Partial<MasterOrderInputs> | null>) => {
			if (action.payload === null) {
				state.masterOrderInputs = initialState.masterOrderInputs;
				return;
			}
			state.masterOrderInputs = { ...initialState.masterOrderInputs, ...action.payload };
		},
		setMasterOrdersRowData(state, action: PayloadAction<MasterOrdersDocument[]>) {
			state.masterOrdersRowData = action.payload;
		},
		setMasterOrderReviewPending: (state, action: PayloadAction<boolean>) => {
			state.masterOrderReviewPending = action.payload;
		},
		setMasterOrderSavedChanges: (state, action: PayloadAction<MasterOrderSavedChanges>) => {
			state.masterOrderSavedChanges = action.payload;
		},
		setMasterOrderStateData: (state, action: PayloadAction<MasterOrderDetailsFragment | null>) => {
			if (action.payload === null) {
				state.masterOrderStateData = undefined;
				return;
			}
			state.masterOrderStateData = { ...action.payload };
		},
		setMasterPickedCustomerId: (state, action: PayloadAction<null | string>) => {
			state.pickedCustomerId = action.payload ?? undefined;
		},
		updateAddMasterLineItem: (state, action: PayloadAction<Partial<StagedMasterLineItem>>) => {
			state.addMasterLineItem = { ...state.addMasterLineItem, ...action.payload };
		},
		/*TODO: set currency based on selected customer*/
		updateMasterCurrency: (state, action: PayloadAction<string | null>) => {
			if (action.payload === null) {
				state.currency = initialState.currency;
				return;
			}
			state.currency = action.payload;
		},
		updateMasterLineItem: (state, action: PayloadAction<StagedMasterLineItem>) => {
			const id = action.payload.id;
			const lineItem = state.masterLineItems?.find((item) => item.id === id);

			if (lineItem) {
				Object.assign(lineItem, action.payload);
			}

			state.addMasterLineItem = initialState.addMasterLineItem;
			state.addMasterLineItemOpen = false;
		},
		updateMasterLineItemWeekQuantity: (state, action: PayloadAction<{ id: string; field: string; value: number }>) => {
			const { field, id, value } = action.payload;
			const lineItem = state.masterLineItems?.find((item) => item.id === id);
			if (lineItem) {
				(lineItem as MasterLineItemWithWeeks)[field as `week_${number}_quantity`] = value;

				lineItem.scheduledQuantity = scheduledEaches(state, lineItem);
			}
		},
		updateMasterOrderContact: (state, action: PayloadAction<Partial<MasterOrderContactDataInput>>) => {
			if (!state.masterOrderCustomer) return;
			Object.assign(state.masterOrderCustomer.contact, action.payload);
		},
		updateMasterOrderCustomer: (state, action: PayloadAction<Partial<MasterOrderCustomerDataInput>>) => {
			if (!state.masterOrderCustomer) return;
			state.masterOrderCustomer = { ...state.masterOrderCustomer, ...action.payload };
		},
		updateMasterOrderInputs: (state, action: PayloadAction<Partial<MasterOrderInputs>>) => {
			Object.assign(state.masterOrderInputs, action.payload);
		},
		upsertMasterOrdersRowData(state, action: PayloadAction<MasterOrdersDocument[]>) {
			const newMasterOrders = action.payload;
			const existingMasterOrderIds = new Set(state.masterOrdersRowData.map((row) => row.masterOrderId));
			const updatedMasterOrders = newMasterOrders.filter((order) => existingMasterOrderIds.has(order.masterOrderId));
			const newMasterOrdersToAdd = newMasterOrders.filter((order) => !existingMasterOrderIds.has(order.masterOrderId));

			state.masterOrdersRowData = state.masterOrdersRowData.map((row) => {
				const updatedOrder = updatedMasterOrders.find((order) => order.masterOrderId === row.masterOrderId);
				return updatedOrder ? { ...row, ...updatedOrder } : row;
			});

			state.masterOrdersRowData = [...state.masterOrdersRowData, ...newMasterOrdersToAdd];
		}
	}
});

export const selectMasterOrderSessionIsClear = (state: { masterOrder: MasterOrderState }): boolean =>
	isEqual(state.masterOrder.masterOrderInputs, initialState.masterOrderInputs);

export const {
	addMasterLineItem,
	clearMasterLineItemsWeekQuantities,
	clearMasterLineItemsWeeks,
	clearMasterOrderInfo,
	clearMasterOrdersRowData,
	closeAddMasterLineItem,
	closeAddMasterLineItemsTable,
	openAddMasterLineItem,
	openAddMasterLineItemsTable,
	removeMasterLineItem,
	setAddMasterLineItem,
	setMasterLineItems,
	setMasterOrderCustomer,
	setMasterOrderInputs,
	setMasterOrdersRowData,
	setMasterOrderReviewPending,
	setMasterOrderSavedChanges,
	setMasterOrderStateData,
	setMasterPickedCustomerId,
	updateAddMasterLineItem,
	updateMasterCurrency,
	updateMasterLineItem,
	updateMasterLineItemWeekQuantity,
	updateMasterOrderContact,
	updateMasterOrderCustomer,
	updateMasterOrderInputs,
	upsertMasterOrdersRowData
} = masterOrderSlice.actions;

export default masterOrderSlice.reducer;
